import React from 'react';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import type { TAccountProduct } from '../../common';
import {
  createMutationPromise,
  CustomFeesSchema,
  errorToMessageString,
  ProductSchema
} from '../../common';
import type { CellDataCustom } from '../../common/utils/table';
import { ComplexRowInputHandler } from '../../components/molecules/forms/ComplexRowInputHandler';
import { mutateProductFees, useAccountStore } from '../../contexts';
import { ButtonContainer, PrimaryButton, SecondaryButton } from '../atoms';
import type { TTableHead } from '../molecules';
import { ComplexTableHeadCell } from '../molecules';
import { first } from 'lodash';
import { EditableTable } from './EditableTable';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import type { FEE_IDS } from '../types';
import { useAutoAnimate } from '@formkit/auto-animate/react';

const ACTIONS_CELL_WIDTH = '105px';
const FIRST_CELL_WIDTH = '190px';
const CUSTOM_FEES = 'customFees';
const FEES = 'fees';

const commonFields = ProductSchema.pick({
  fees: true
}).augment({
  customFees: CustomFeesSchema
});

type EditFormSchema = typeof commonFields;
type EditFormData = z.infer<typeof commonFields>;

type Props = {
  product: TAccountProduct;
  merchantId: string;
  feeId: FEE_IDS;
  // columns: ReadonlyArray<ReadonlyArray<AccountProductBlockRow>>;
  itemTitle?: string;
  itemSubtitle?: string;
  onUpdate?: (terms: any) => Promise<unknown>;
  usePercent?: boolean;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const FlexHeadWrapper = styled.span<TTableHead>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    > span:nth-child(2) {
      margin-right: -15px;
    }
  `
);

export const BaseInitialFeeMatrix = ({
  product,
  merchantId,
  feeId,
  itemSubtitle = 'Account Servicing Fees',
  usePercent = false
}: Props) => {
  const [parent] = useAutoAnimate();
  const contentType = usePercent
    ? ('percent*100' as const)
    : ('currency' as const);
  const getCellsData = (): Array<Array<CellDataCustom>> => {
    return [
      [
        {
          contentType: 'text' as const,
          nonEditable: true,
          content: 'All Amounts'
        },
        {
          contentType: contentType,
          content: Number(get(product, `fees.${feeId}.amount`, 0))
        }
      ]
    ];
  };

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    control,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<EditFormSchema>({
    resolver: zodResolver(commonFields),
    mode: 'onBlur', // revalidate onBlur
    defaultValues: {
      [FEES]: product[FEES],
      customFees: getCellsData()
    }
  });

  const updateProductFees = useAccountStore((s) => s.updateProductFees);
  const updateProductFeesMutation = mutateProductFees({
    productId: product.id,
    merchantId: merchantId,
    updateProductFees,
    mutate: useMutation
  });

  const onSubmit: SubmitHandler<EditFormSchema> = async (data) => {
    const fees: TAccountProduct['fees'] = {
      ...product.fees,
      [feeId]: {
        ...product.fees[feeId],
        amount: Number(data.customFees[0][1].content) as number
      }
    };

    try {
      const productFeesChanged = !isEqual(fees, product.fees);

      if (productFeesChanged) {
        await createMutationPromise(updateProductFeesMutation, fees);
      }

      if (productFeesChanged) {
        toast.success(`Successfully Updated ${itemSubtitle}`);
      } else {
        // Probably more logical to show now popup at all if they don't make any changes
        // toast.success(`No changes made to ${product.productType}`);
      }
    } catch (err) {
      toast.error(errorToMessageString(err));
    }
  };

  const handleReset = () => {
    reset();
  };

  const onSubmitError: SubmitErrorHandler<EditFormSchema> = (err) => {
    console.error(err);
    toast.error('Something went wrong while trying to submit the form.');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onSubmitError)}>
      <ComplexRowInputHandler
        key={`${first(getValues(CUSTOM_FEES))?.length || 0}`}
        row={{
          name: CUSTOM_FEES,
          title: '',
          content: [],
          contentType: 'table',
          nonEditable: false,
          tableConfig: {
            getValues,
            // title: itemSubtitle,
            hideDeleteButton: true
          }
        }}
        control={control}
        errors={errors}
        register={register}
      >
        <EditableTable.TableHeader>
          <ComplexTableHeadCell
            style={{
              minWidth: FIRST_CELL_WIDTH,
              maxWidth: FIRST_CELL_WIDTH,
              width: FIRST_CELL_WIDTH
            }}
          >
            <FlexHeadWrapper>
              <span>Loan Amount</span>
            </FlexHeadWrapper>
          </ComplexTableHeadCell>
          <ComplexTableHeadCell>
            <FlexHeadWrapper>
              <span>All Terms</span>
            </FlexHeadWrapper>
          </ComplexTableHeadCell>
          <ComplexTableHeadCell
            style={{
              minWidth: ACTIONS_CELL_WIDTH,
              maxWidth: ACTIONS_CELL_WIDTH,
              width: ACTIONS_CELL_WIDTH
            }}
          ></ComplexTableHeadCell>
        </EditableTable.TableHeader>
      </ComplexRowInputHandler>
      <div ref={parent}>
        {isDirty && (
          <FormFooter
            horizontalSpacing="10px"
            verticalSpacing="0px"
            justify="end"
          >
            <SecondaryButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={!isDirty || isSubmitting}
              isLoading={isSubmitting}
            >
              Save Fee
            </PrimaryButton>
          </FormFooter>
        )}
      </div>
    </form>
  );
};
